import { UnavailableItemsByName } from '../../../redux/features/menuUnification/menuUnification.props';
import { UnavailabilityReason } from '../../../constants/brandMenu';
import { CartItem } from '../../../utils/cart';
import { sortModGroups } from '../../../utils/menu';
import {
  isBMItemConstraintNotAvailable,
  isBMItemNotSold,
} from '../../../utils/menuUnification.utils';
import { IModGroup, SelectMenuItemModifier } from './modGroup.props';
import { ParsedModifierGroup } from '../../../types/menu';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import logger from '../../../utils/logger';

const hasModGroupChildren = (props: IModGroup) => {
  const { modGroup, cartModGroup, cartItem } = props;

  const modChildren = Object.values(cartModGroup?.selectedItems || {}).filter(
    (selectedModifierItem) => {
      const modGroupChildren = sortModGroups(
        modGroup.menuItems[selectedModifierItem.itemId]
      );
      const filteredModGroups = Object.values(modGroupChildren).filter(
        (mod) => !cartItem.modifierGroups[mod.id]
      );
      return filteredModGroups.length;
    }
  );

  return !!modChildren.length;
};

const canShowBMItem = ({
  unavailableItemsByName,
  itemName,
  cartItem,
  modGroupName,
}: {
  unavailableItemsByName: UnavailableItemsByName;
  itemName: string;
  cartItem: CartItem;
  modGroupName: string;
}) => {
  // If the item is globally not sold, hide it
  if (isBMItemNotSold(unavailableItemsByName, itemName)) {
    return false;
  }
  // If the item is not sold due to a constraint, hide it
  if (
    isBMItemConstraintNotAvailable({
      unavailableItemsByName,
      itemName,
      cartItem,
      constraintName: modGroupName,
      unavailabilityReason: UnavailabilityReason.notSold,
    })
  ) {
    return false;
  }
  // Otherwise, show the item
  return true;
};

/**
 * Determines if a menu item should be shown based on brand menu logic and item availability.
 */
const canShowItem = ({
  isBrandMenu,
  unavailableItemsByName,
  itemName,
  cartItem,
  modGroupName,
  childAvailability,
  is86edToday,
}: {
  isBrandMenu: boolean;
  unavailableItemsByName: UnavailableItemsByName;
  itemName: string;
  cartItem: CartItem;
  modGroupName: string;
  childAvailability: boolean;
  is86edToday: boolean;
}) => {
  if (isBrandMenu) {
    return canShowBMItem({
      unavailableItemsByName,
      itemName,
      cartItem,
      modGroupName,
    });
  }
  // For non-brand menu, show if available or 86ed today
  return !!(childAvailability || is86edToday);
};

// Shown instead of speaking the heading's description when express mode has
// suppressed this upsell prompt. A silently dead label would read as broken
// TTS and invite a second click, so say why the click did nothing (HITL-1529).
const EXPRESS_SUPPRESSED_LABEL_TOOLTIP =
  'Express mode is ON — this upsell is suppressed, so its description is not read out.';

export {
  hasModGroupChildren,
  canShowItem,
  canShowBMItem,
  EXPRESS_SUPPRESSED_LABEL_TOOLTIP,
};

/**
 * Applies a group's menu defaults, as a selection the AI and the agent both
 * see as "nobody chose this". The caller decides WHEN: ModGroup runs it from
 * its mount effect while the cart has no entry for the group yet, and the
 * repeated-slot panel runs it per slot under the same condition, so a set
 * drawn as a panel builds exactly as its rows would have.
 *
 * Moved verbatim out of ModGroup's effect.
 */
export const applyModGroupDefaults = ({
  cartItem,
  modGroup,
  pathToItem,
  parentModifier,
  isBrandMenu,
  unavailableItemsByName,
  selectMenuItemModifier,
}: {
  cartItem: CartItem;
  modGroup: ParsedModifierGroup;
  pathToItem: string;
  parentModifier: string;
  isBrandMenu: boolean;
  unavailableItemsByName: UnavailableItemsByName;
  selectMenuItemModifier: SelectMenuItemModifier;
}) => {
  modGroup.defaultSelectedItemIds?.forEach((itemId: string | number) => {
    if (
      isBrandMenu &&
      isBMModifierAnUnavailableConstraint(
        cartItem,
        unavailableItemsByName,
        parentModifier,
        pathToItem,
        modGroup.name,
        String(itemId)
      )
    ) {
      // Skip: this brand-menu default item is an unavailable constraint.
    } else {
      const matchingItems = Object.values(modGroup.menuItems).filter(
        (item) => item.itemId === String(itemId)
      );

      if (!matchingItems.length) {
        logger.warn({
          message: `Warning: Default item ${itemId} not found in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
        });
      } else if (matchingItems.length > 1) {
        logger.warn({
          message: `Warning: Multiple items found with itemId ${itemId} in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
        });
      }

      // HITL-1708: an option 86'd or meal-period inactive everywhere is
      // never a default — same rule as add time (processDefaultSelectedModifiers).
      matchingItems
        .filter((item) => item.available !== false)
        .forEach((item) =>
          selectMenuItemModifier(item, modGroup, pathToItem, true, false, true)
        );
    }
  });
};
